// =============================================================================
// SIREPPAR - Página de Interacciones con Partidos
// =============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import DataTable, { Column } from '../../components/common/DataTable';
import PestaniasComunicaciones from '../../components/common/PestaniasComunicaciones';
import { interaccionService } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { puedeEscribirInteracciones, puedeEliminarInteracciones } from '../../utils/permisosInteracciones';

// =============================================================================
// Tipos
// =============================================================================

interface Interaccion {
  id: string;
  tipo: string;
  contactoNombre: string;
  contactoCargo?: string;
  fechaInteraccion: string;
  resumen: string;
  estado: string;
  prioridad: string;
  extrapartidaria: boolean;
  archivadaEn: string | null;
  partido: { id: string; denominacion: string; numero: number };
  expediente?: { id: string; numero: string };
}

// =============================================================================
// Constantes
// =============================================================================

const tipoColors: Record<string, string> = {
  WHATSAPP:     'bg-green-100 text-green-800',
  LLAMADA:      'bg-blue-100 text-blue-800',
  EMAIL:        'bg-yellow-100 text-yellow-800',
  PRESENCIAL:   'bg-purple-100 text-purple-800',
  VIDEOLLAMADA: 'bg-indigo-100 text-indigo-800',
  NOTA_INTERNA: 'bg-gray-100 text-gray-800',
};

const tipoLabels: Record<string, string> = {
  WHATSAPP:     'WhatsApp',
  LLAMADA:      'Llamada',
  EMAIL:        'Email',
  PRESENCIAL:   'Presencial',
  VIDEOLLAMADA: 'Videollamada',
  NOTA_INTERNA: 'Nota interna',
};

const estadoColors: Record<string, string> = {
  PENDIENTE: 'bg-yellow-100 text-yellow-800',
  RESUELTO:  'bg-green-100 text-green-800',
};

const estadoLabels: Record<string, string> = {
  PENDIENTE: 'Pendiente',
  RESUELTO:  'Resuelto',
};

const prioridadColors: Record<string, string> = {
  ALTA:  'bg-red-100 text-red-800',
  MEDIA: 'bg-yellow-100 text-yellow-800',
  BAJA:  'bg-green-100 text-green-800',
};

const prioridadLabels: Record<string, string> = {
  ALTA:  'Alta',
  MEDIA: 'Media',
  BAJA:  'Baja',
};

// =============================================================================
// Columnas
// =============================================================================

const columns: Column<Interaccion>[] = [
  {
    key: 'fechaInteraccion',
    header: 'Fecha',
    render: (value) => (
      <div>
        <div>{new Date(value).toLocaleDateString('es-AR')}</div>
        <div className="text-xs text-gray-400">
          {new Date(value).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs
        </div>
      </div>
    ),
  },
  {
    key: 'partido',
    header: 'Partido',
    render: (value) => value?.denominacion ?? '-',
  },
  {
    key: 'tipo',
    header: 'Tipo',
    render: (value, row) => (
      <div className="flex flex-col gap-1">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${tipoColors[value] ?? 'bg-gray-100 text-gray-700'}`}>
          {tipoLabels[value] ?? value}
        </span>
        {row.extrapartidaria && (
          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
            Extrapartidaria
          </span>
        )}
      </div>
    ),
  },
  {
    key: 'contactoNombre',
    header: 'Contacto',
    render: (value, row) => (
      <div>
        <div>{value}</div>
        {row.contactoCargo && <div className="text-xs text-gray-400">{row.contactoCargo}</div>}
      </div>
    ),
  },
  {
    key: 'resumen',
    header: 'Resumen',
    className: 'max-w-xs truncate',
  },
  {
    key: 'estado',
    header: 'Estado',
    render: (value) => (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${estadoColors[value] ?? 'bg-gray-100 text-gray-700'}`}>
        {estadoLabels[value] ?? value}
      </span>
    ),
  },
  {
    key: 'prioridad',
    header: 'Prioridad',
    render: (value) => (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${prioridadColors[value] ?? 'bg-gray-100 text-gray-700'}`}>
        {prioridadLabels[value] ?? value}
      </span>
    ),
  },

];

// =============================================================================
// Componente
// =============================================================================

export default function InteraccionesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const puedeEscribir = puedeEscribirInteracciones(user?.rol);
  const puedeEliminar = puedeEliminarInteracciones(user?.rol);
  const [searchParams] = useSearchParams();
  const prefilledPartidoId = searchParams.get('partidoId') ?? '';  
  const queryClient = useQueryClient();

  const [filtros, setFiltros] = useState({
    tipo: '',
    estado: '',
    fechaDesde: '',
    fechaHasta: '',
    contactoNombre: '',
    detalle: '',
    prioridad: '',
    extrapartidaria: '',
    archivadas: '',
  });

  const params = {
    ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== '')),
    ...(prefilledPartidoId ? { partidoId: prefilledPartidoId } : {}),
  };  

  const { data, isLoading } = useQuery({
    queryKey: ['interacciones', params],
    queryFn: () => interaccionService.list(params),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => interaccionService.delete(id),
    onSuccess: () => {
      toast.success('Interacción eliminada');
      queryClient.invalidateQueries({ queryKey: ['interacciones'] });
    },
  });

  const archivarMutation = useMutation({
    mutationFn: (id: string) => interaccionService.archivar(id),
    onSuccess: () => {
      toast.success('Interacción archivada');
      queryClient.invalidateQueries({ queryKey: ['interacciones'] });
    },
  });
  const desarchivarMutation = useMutation({
    mutationFn: (id: string) => interaccionService.desarchivar(id),
    onSuccess: () => {
      toast.success('Interacción desarchivada');
      queryClient.invalidateQueries({ queryKey: ['interacciones'] });
    },
  });
  
  const interacciones: Interaccion[] = data?.data ?? [];

  const handleFiltro = (key: string, value: string) => {
    setFiltros(prev => ({ ...prev, [key]: value }));
  };

  const columnsWithActions: Column<Interaccion>[] = [
    ...columns,
    {
      key: 'id',
      header: 'Acciones',
      render: (value) => (
        <div className="flex gap-3">
                {puedeEscribir && (filtros.archivadas === 'true' ? (
          <button
            onClick={(e) => { e.stopPropagation(); if (confirm('¿Desarchivar esta interacción?')) desarchivarMutation.mutate(value); }}
            className="text-blue-600 hover:underline text-xs"
          >
            Desarchivar
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); if (confirm('¿Archivar esta interacción?')) archivarMutation.mutate(value); }}
            className="text-orange-600 hover:underline text-xs"
          >
            Archivar
          </button>
        ))}
        {puedeEliminar && (
        <button
          onClick={(e) => { e.stopPropagation(); if (confirm('¿Eliminar esta interacción?')) deleteMutation.mutate(value); }}
          className="text-red-600 hover:underline text-xs"
        >
          Eliminar
        </button>
        )}
      </div>
      ),
    },
  ];

  return (
    <div>
      <PestaniasComunicaciones />

      {/* Encabezado */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Interacciones con partidos</h1>
          <p className="text-sm text-gray-500 mt-1">Registro de comunicaciones con apoderados y autoridades</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleFiltro('archivadas', filtros.archivadas === 'true' ? '' : 'true')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${
              filtros.archivadas === 'true'
                ? 'bg-orange-50 border-orange-300 text-orange-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {filtros.archivadas === 'true' ? '← Volver a activas' : 'Ver archivadas'}
          </button>
          {puedeEscribir && (
          <button
            onClick={() => navigate('/interacciones/nueva')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Nueva interacción
          </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex flex-wrap gap-3">
        <select
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700"
          value={filtros.tipo}
          onChange={e => handleFiltro('tipo', e.target.value)}
        >
          <option value="">Todos los tipos</option>
          {Object.entries(tipoLabels).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <select
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700"
          value={filtros.estado}
          onChange={e => handleFiltro('estado', e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="RESUELTO">Resuelto</option>
        </select>

        <input
          type="text"
          placeholder="Nombre del contacto..."
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 w-48"
          value={filtros.contactoNombre}
          onChange={e => handleFiltro('contactoNombre', e.target.value)}
        />
        
        <input
          type="text"
          placeholder="Buscar en notas..."
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 w-48"
          value={filtros.detalle}
          onChange={e => handleFiltro('detalle', e.target.value)}
        />

        <input
          type="date"
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700"
          value={filtros.fechaDesde}
          onChange={e => handleFiltro('fechaDesde', e.target.value)}
        />
        <input
          type="date"
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700"
          value={filtros.fechaHasta}
          onChange={e => handleFiltro('fechaHasta', e.target.value)}
        />

        <select
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700"
          value={filtros.prioridad}
          onChange={e => handleFiltro('prioridad', e.target.value)}
        >
          <option value="">Todas las prioridades</option>
          <option value="ALTA">Alta</option>
          <option value="MEDIA">Media</option>
          <option value="BAJA">Baja</option>
        </select>

        <select
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700"
          value={filtros.extrapartidaria}
          onChange={e => handleFiltro('extrapartidaria', e.target.value)}
        >
          <option value="">Todas</option>
          <option value="false">Partidarias</option>
          <option value="true">Extrapartidarias</option>
        </select>

        {Object.values(filtros).some(v => v !== '') && (
          <button
          onClick={() => setFiltros({ tipo: '', estado: '', fechaDesde: '', fechaHasta: '', contactoNombre: '', detalle: '', prioridad: '', extrapartidaria: '', archivadas: '' })}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla */}
      <DataTable
        data={interacciones}
        columns={columnsWithActions}
        loading={isLoading}
        emptyMessage="No hay interacciones registradas"
        onRowClick={(row) => navigate(`/interacciones/${row.id}`)}
      />

      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
        <span>{interacciones.length} registro{interacciones.length !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}
